import { useEffect } from "react";

import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UrlList } from "@/pages/tina/components/url-list";
import { useDiscoverTest } from "@/pages/tina/sites/hooks/use-discover-test";
import type { DiscoverTestReport } from "@/types/tina";

interface DiscoverTestDialogProps {
  siteName: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function ReportView({ report }: { report: DiscoverTestReport }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge>{report.strategy}</Badge>
        <span className="text-muted-foreground">status: {report.status}</span>
        <span className="text-muted-foreground">
          {report.discovered_count} discovered · {report.new_count} new ·{" "}
          {report.known_count ?? "—"} known
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        Nothing was written to the database — this is a dry run.
      </p>
      <UrlList title="New URLs" urls={report.new_urls} tone="new" />
      <UrlList title="Known URLs" urls={report.known_urls} tone="known" />
      <UrlList title="All discovered" urls={report.discovered_urls} tone="all" />
    </div>
  );
}

export function DiscoverTestDialog({
  siteName,
  open,
  onOpenChange,
}: DiscoverTestDialogProps) {
  const { start, reset, starting, data, isLoading } = useDiscoverTest(
    siteName ?? "",
  );

  // Kick off a test whenever the dialog opens for a site.
  useEffect(() => {
    if (open && siteName) {
      start();
    } else if (!open) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, siteName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Discover test{siteName ? ` · ${siteName}` : ""}</DialogTitle>
          <DialogDescription>
            Runs discovery for this site and reports the URLs it would find.
          </DialogDescription>
        </DialogHeader>

        {starting || isLoading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Running discovery (worker cold start can take up to ~2 min)…
          </div>
        ) : data?.status === "failed" ? (
          <div className="p-4 text-sm text-destructive">
            Discover test failed: {String(data.error?.["message"] ?? "unknown error")}
          </div>
        ) : data?.status === "done" && data.report ? (
          <ReportView report={data.report} />
        ) : (
          <div className="p-6 text-sm text-muted-foreground">Starting…</div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => start()} disabled={starting || isLoading}>
            Re-run
          </Button>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
